import { ProfileOutlined, QuestionCircleOutlined, RocketOutlined } from '@ant-design/icons'
import { useRequest } from 'ahooks'
import { Col, Descriptions, Row, Tooltip, Typography } from 'antd'
import { isNil } from 'lodash'
import moment from 'moment'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { Editor, MosaicComponent, getPointPermission } from '@/components'
import { ParentRelations } from '@/components/parent-relations.component'
import { PriorityPopoverDescription } from '@/components/priority-popover.component'
import { ResourceUsers } from '@/components/resouce-users/resource-users.component'
import { ResourceOKR } from '@/components/resource-okr.component'
import { NormalResourceList } from '@/modules/portfolio/portfolio-list'
import { ResourceReport } from '@/modules/project/project-detail/project-summary'
import {
  PAGE_RESOURCE_POINTS_ENUM,
  Portfolio,
  PriorityEnum,
  ProjectClassification,
  ResourceType,
  ResourceUser,
} from '@/schemas'

const { Title } = Typography

interface PortfolioSummaryContainerProps {
  portfolio: Portfolio
  setUsers?: React.Dispatch<React.SetStateAction<ResourceUser[] | undefined>>
}

export const PortfolioSummaryContainer: React.FC<PortfolioSummaryContainerProps> = ({ portfolio, setUsers }) => {
  const selfId = portfolio.selfId
  const { t } = useTranslation()

  const { run } = useRequest(
    (data) => ({
      url: '/api/portfolio/updatePortfolio',
      method: 'patch',
      data: {
        ...data,
        selfId: portfolio.selfId,
        type: portfolio.type,
      },
    }),
    {
      manual: true,
      debounceInterval: 800,
    }
  )

  return (
    <>
      <Row gutter={[0, 32]}>
        <Col xs={24} md={16} className='children:mb-24px'>
          <Descriptions column={3} size='small' className='mt-8px'>
            {/* <Descriptions.Item label={t('department')}>
              <DepartmentName
                canRead={getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_所属部门, portfolio.permissions).canRead}
                departmentId={portfolio.departmentId}
              />
            </Descriptions.Item> */}
            <Descriptions.Item
              label={
                <span>
                  {t('devlop.detail.priority')}
                  <PriorityPopoverDescription />
                </span>
              }
            >
              {!getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_优先级, portfolio.permissions).canRead ? (
                <MosaicComponent />
              ) : !isNil(portfolio.priority) ? (
                t(`form.priority.${PriorityEnum[portfolio.priority]}`)
              ) : (
                t('无')
              )}
            </Descriptions.Item>
            <Descriptions.Item label={t('portfolio.classification')}>
              {!getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_分类, portfolio.permissions).canRead ? (
                <MosaicComponent />
              ) : portfolio.classification ? (
                t(ProjectClassification[portfolio.classification])
              ) : null}
            </Descriptions.Item>
            <Descriptions.Item label={t('cycle')}>
              {!getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_周期, portfolio.permissions).canRead ? (
                <MosaicComponent />
              ) : (
                <>
                  {portfolio.startDate ? moment(portfolio.startDate).format('YYYY-MM-DD') : 'N/A'} -{' '}
                  {portfolio.endDate ? moment(portfolio.endDate).format('YYYY-MM-DD') : 'N/A'}
                </>
              )}
            </Descriptions.Item>
          </Descriptions>

          {/* <div>
            <Title level={4}>
              <ProfileOutlined className='mr-8px' /> {t('portfolio.description')}
            </Title>

            <Editor
              editorKey={portfolio.selfId}
              editable={false}
              content={portfolio.description}
              onSave={(description) => run({ description })}
            />
          </div> */}

          <div>
            <Title level={4}>
              <ProfileOutlined className='mr-8px' />
              {t('resource.background')}
              <Tooltip title={t('devlop.form.background.tooltip')}>
                <QuestionCircleOutlined className='ml-4px text-gray-500 cursor-pointer' />
              </Tooltip>
            </Title>

            <Editor
              editorKey={portfolio.selfId}
              editable={getPointPermission(PAGE_RESOURCE_POINTS_ENUM['概况_背景'], portfolio.permissions).editable}
              canRead={getPointPermission(PAGE_RESOURCE_POINTS_ENUM['概况_背景'], portfolio.permissions).canRead}
              content={portfolio.background}
              onSave={(background) => run({ background })}
            />
          </div>

          <div>
            <Title level={4}>
              <RocketOutlined className='mr-8px' />
              {t('resource.goalDesc')}
              <Tooltip title={t('devlop.form.goal.tooltip')}>
                <QuestionCircleOutlined className='ml-4px text-gray-500 cursor-pointer' />
              </Tooltip>
            </Title>

            <Editor
              editorKey={portfolio.selfId}
              className='mb-12px'
              editable={getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_业务目标, portfolio.permissions).editable}
              canRead={getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_业务目标, portfolio.permissions).canRead}
              content={portfolio.goal}
              onSave={(goal) => run({ goal })}
            />
          </div>

          <div>
            <Title level={4}>
              <ProfileOutlined className='mr-8px' />
              {t('resource.checkStandard')}
              <Tooltip title={t('devlop.form.standard.tooltip')}>
                <QuestionCircleOutlined className='ml-4px text-gray-500 cursor-pointer' />
              </Tooltip>
            </Title>

            <Editor
              editorKey={portfolio.selfId}
              editable={
                getPointPermission(PAGE_RESOURCE_POINTS_ENUM['概况_交付物/验收标准'], portfolio.permissions).editable
              }
              canRead={
                getPointPermission(PAGE_RESOURCE_POINTS_ENUM['概况_交付物/验收标准'], portfolio.permissions).canRead
              }
              content={portfolio.standard}
              onSave={(standard) => run({ standard })}
            />
          </div>

          <div>
            <Title level={4}>
              <ProfileOutlined className='mr-8px' />
              {t('resource.challenge')}
              <Tooltip title={t('resource.challengeTooltip')}>
                <QuestionCircleOutlined className='ml-4px text-gray-500 cursor-pointer' />
              </Tooltip>
            </Title>

            <Editor
              editorKey={portfolio.selfId}
              editable={getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_挑战性, portfolio.permissions).editable}
              canRead={getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_挑战性, portfolio.permissions).canRead}
              content={portfolio.challenge}
              onSave={(challenge) => run({ challenge })}
            />
          </div>

          <ResourceOKR resource={portfolio} isSummaryPage={true} />

          <ParentRelations resource={portfolio} selfId={selfId} direction='up' type={ResourceType.PORTFOLIO} />

          <ResourceUsers isShowTip={false} source={portfolio} setUsers={setUsers} />
        </Col>

        <Col xs={24} md={8}>
          <ResourceReport resource={portfolio} />
        </Col>
      </Row>

      <NormalResourceList resource={portfolio} pageType={ResourceType.PORTFOLIO} />
    </>
  )
}
